import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package, Loader2, Image, User, Sparkles, X, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import FollowButton from '../components/FollowButton';
import { BACKEND_URL } from '../config/api';

export default function InspirationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [stashItems, setStashItems] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Fetch stash items
  useEffect(() => {
    const fetchStash = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/stash`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setStashItems(Array.isArray(data) ? data : (data.items || []));
        }
      } catch {
        // Optional
      }
    };
    fetchStash();
  }, []);

  const handleSearch = useCallback(async () => {
    if (!query.trim() && selectedProducts.length === 0) return;
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set('q', query.trim());
      if (selectedProducts.length > 0) {
        params.set('products', JSON.stringify(selectedProducts.map(p => ({
          name: p.name, brand: p.brand || ''
        }))));
      }

      const res = await fetch(`${BACKEND_URL}/api/inspiration/search?${params}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setResults(await res.json());
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  }, [query, selectedProducts]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const toggleProduct = (item) => {
    const exists = selectedProducts.find(p => p.item_id === item.item_id);
    if (exists) {
      setSelectedProducts(prev => prev.filter(p => p.item_id !== item.item_id));
    } else if (selectedProducts.length < 10) {
      setSelectedProducts(prev => [...prev, {
        item_id: item.item_id,
        name: item.name,
        brand: item.brand || '',
      }]);
    }
  };

  const filteredStash = stashItems.filter(item => {
    if (!productSearch.trim()) return true;
    const q = productSearch.toLowerCase();
    return (item.name || '').toLowerCase().includes(q) || (item.brand || '').toLowerCase().includes(q);
  });

  const renderGroup = (title, designs, emptyText) => {
    if (!designs || designs.length === 0) return null;
    return (
      <div className="mb-8">
        <h3 className="text-sm font-medium text-charcoal mb-3">{title}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {designs.map(design => (
            <div
              key={design.design_id}
              className="bg-white rounded-xl border border-beige overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/designs/${design.design_id}`)}
              data-testid={`inspiration-card-${design.design_id}`}
            >
              <div className="aspect-[4/3] bg-cream relative">
                {design.image_url ? (
                  <img src={design.image_url} alt={design.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image size={32} className="text-beige" />
                  </div>
                )}
                {/* Similarity badge */}
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-sage/80 text-white rounded-full text-xs">
                  {Math.round(design.similarity * 100)}% match
                </div>
              </div>
              <div className="p-3">
                <h4 className="font-medium text-charcoal text-sm truncate">{design.title}</h4>
                {design.creator && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {design.creator.picture ? (
                      <img src={design.creator.picture} alt="" className="w-4 h-4 rounded-full" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-sage-light flex items-center justify-center">
                        <User size={8} className="text-sage" />
                      </div>
                    )}
                    <span className="text-xs text-gray-soft">
                      {design.creator.handle ? `@${design.creator.handle}` : design.creator.name}
                    </span>
                  </div>
                )}
                {design.technique_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {design.technique_tags.slice(0, 2).map(tag => (
                      <span key={tag} className="px-1.5 py-0.5 bg-sage/10 text-sage-dark text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-light text-charcoal" data-testid="inspiration-title">Find Inspiration</h1>
          <p className="text-sm text-gray-soft mt-1">Search designs by description or products from your stash</p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl border border-beige p-4 mb-6">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe what you want to create... (e.g., watercolor birthday card)"
                className="w-full pl-9 pr-3 py-2.5 border border-beige rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage"
                data-testid="inspiration-search-input"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching || (!query.trim() && selectedProducts.length === 0)}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                searching || (!query.trim() && selectedProducts.length === 0)
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-sage text-white hover:bg-sage-dark'
              }`}
              data-testid="search-btn"
            >
              {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Search
            </button>
          </div>

          {/* Product hints */}
          <div className="mt-3">
            <button
              onClick={() => setShowProductPicker(!showProductPicker)}
              className="flex items-center gap-1.5 text-xs text-sage hover:text-sage-dark transition-colors"
              data-testid="toggle-product-picker"
            >
              <Package size={12} />
              {selectedProducts.length > 0
                ? `${selectedProducts.length} product hint${selectedProducts.length > 1 ? 's' : ''}`
                : 'Add product hints from your stash'}
              {showProductPicker ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>

            {selectedProducts.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedProducts.map(p => (
                  <span key={p.item_id} className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-full flex items-center gap-1">
                    {p.name}
                    <button onClick={() => toggleProduct(p)} className="hover:text-red-500">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {showProductPicker && (
              <div className="mt-2 border border-beige rounded-lg overflow-hidden">
                <div className="p-2 border-b border-beige">
                  <input
                    type="text"
                    value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    placeholder="Search your stash..."
                    className="w-full px-2 py-1.5 text-sm border border-beige rounded focus:outline-none focus:border-sage"
                  />
                </div>
                <div className="max-h-32 overflow-y-auto">
                  {filteredStash.slice(0, 20).map(item => {
                    const selected = selectedProducts.some(p => p.item_id === item.item_id);
                    return (
                      <button
                        key={item.item_id}
                        onClick={() => toggleProduct(item)}
                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-cream ${
                          selected ? 'bg-sage/5' : ''
                        }`}
                      >
                        <span className="text-charcoal">{item.name} {item.brand ? `(${item.brand})` : ''}</span>
                        {selected && <span className="text-sage font-medium">Selected</span>}
                      </button>
                    );
                  })}
                  {stashItems.length === 0 && (
                    <p className="px-3 py-2 text-xs text-gray-soft">No items in your stash</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        {results && (
          <div data-testid="inspiration-results">
            {results.total === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-beige">
                <Search size={48} className="mx-auto text-beige mb-4" />
                <h3 className="text-lg font-medium text-charcoal mb-2">No matching designs found</h3>
                <p className="text-sm text-gray-soft max-w-md mx-auto">
                  Try different keywords or browse the community feed for inspiration.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-soft mb-4">
                  Found {results.total} design{results.total !== 1 ? 's' : ''} matching "{results.query}"
                </p>
                {renderGroup("Your Designs", results.groups?.mine)}
                {renderGroup("From People You Follow", results.groups?.following)}
                {renderGroup("Community Designs", results.groups?.community)}
              </>
            )}
          </div>
        )}

        {/* Empty state - before search */}
        {!results && !searching && (
          <div className="text-center py-12 bg-white rounded-xl border border-beige">
            <Sparkles size={48} className="mx-auto text-sage/30 mb-4" />
            <h3 className="text-lg font-medium text-charcoal mb-2">Find your next project idea</h3>
            <p className="text-sm text-gray-soft max-w-md mx-auto">
              Describe what you want to create, or add products from your stash to find designs that use them.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
